import { useMemo, useState, useEffect } from "react";
import api from "@/services/api";
import { useNotifications } from "@/context/NotificationContext";

interface ClientDetails {
  id: string;
  company_name: string;
  email: string;
  full_name: string;
  plan: string;
  status: string;
  industry?: string | null;
  mrr: number;
  user_id?: string;
}

interface UserData {
  one_time_password?: string;
  otp_used?: boolean;
}

export default function ClientDetailsModal({
  open,
  client,
  onClose,
}: {
  open: boolean;
  client: ClientDetails | null;
  onClose: () => void;
}) {
  const { showToast } = useNotifications();
  const [copied, setCopied] = useState(false);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [editingOtp, setEditingOtp] = useState(false);
  const [newOtp, setNewOtp] = useState("");
  const [resetOtpUsed, setResetOtpUsed] = useState(false);

  useEffect(() => {
    if (client?.user_id) {
      fetchUserData(client.user_id);
    }
  }, [client?.user_id]);

  const fetchUserData = async (userId: string) => {
    try {
      const response = await api.get(`/users/${userId}`);
      setUserData(response.data.data);
    } catch (error) {
      console.error("Failed to fetch user data:", error);
    }
  };

  const handleUpdateOtp = async () => {
    if (!client?.user_id || !newOtp) return;
    try {
      await api.patch(`/users/${client.user_id}/otp`, {
        one_time_password: newOtp,
        reset_otp_used: resetOtpUsed,
      });
      showToast("success", "OTP updated successfully");
      setEditingOtp(false);
      setNewOtp("");
      setResetOtpUsed(false);
      fetchUserData(client.user_id);
    } catch (error) {
      showToast("error", "Failed to update OTP");
    }
  };

  const title = useMemo(() => {
    if (!client) return "Client";
    return client.company_name;
  }, [client]);

  if (!open || !client) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-dark-panel border border-dark-border rounded-2xl p-6 max-w-xl w-full animate-slide-up">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="text-white font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Client details
            </h3>
            <p className="text-silver-dim text-sm mt-1">{title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-lg border border-dark-border text-silver-dim hover:text-white hover:border-electric-blue/40 transition-colors cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Company</p>
            <p className="text-white text-sm font-semibold mt-1">{client.company_name}</p>
          </div>
          <div>
            <p className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Plan</p>
            <p className="text-white text-sm font-semibold mt-1 capitalize">{client.plan}</p>
          </div>
          <div>
            <p className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Status</p>
            <p className="text-white text-sm font-semibold mt-1 capitalize">{client.status}</p>
          </div>
          <div>
            <p className="text-silver-dim text-xs font-semibold uppercase tracking-wider">MRR</p>
            <p className="text-white text-sm font-semibold mt-1">${client.mrr}/mo</p>
          </div>
          <div>
            <p className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Full name</p>
            <p className="text-white text-sm font-semibold mt-1">{client.full_name}</p>
          </div>
          <div>
            <p className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Email</p>
            <p className="text-white text-sm font-semibold mt-1">{client.email}</p>
          </div>
          <div className="sm:col-span-2">
            <p className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Industry</p>
            <p className="text-silver text-sm mt-1">{client.industry || "—"}</p>
          </div>
          <div className="sm:col-span-2">
            <div className="flex items-center justify-between mb-2">
              <p className="text-silver-dim text-xs font-semibold uppercase tracking-wider">One-Time Password</p>
              <button
                onClick={() => setEditingOtp(!editingOtp)}
                className="text-electric-blue text-xs hover:underline cursor-pointer"
              >
                {editingOtp ? "Cancel" : "Edit"}
              </button>
            </div>
            {editingOtp ? (
              <div className="space-y-3">
                <input
                  type="text"
                  value={newOtp}
                  onChange={(e) => setNewOtp(e.target.value)}
                  placeholder="Enter new OTP"
                  className="w-full bg-dark-card border border-dark-border rounded-lg px-3 py-2 text-white text-sm placeholder-silver-dim focus:outline-none focus:border-electric-blue/50 transition-colors"
                />
                <label className="flex items-center gap-2 text-silver text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={resetOtpUsed}
                    onChange={(e) => setResetOtpUsed(e.target.checked)}
                    className="w-4 h-4 rounded border-dark-border bg-dark-card text-electric-blue focus:ring-electric-blue/50"
                  />
                  Reset OTP used status (client will need OTP again)
                </label>
                <button
                  onClick={handleUpdateOtp}
                  className="w-full bg-electric-blue text-deep-black font-semibold px-4 py-2 rounded-lg text-sm hover:shadow-glow-blue transition-all cursor-pointer"
                >
                  Update OTP
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <p className="text-white text-sm font-semibold">
                  {userData?.one_time_password || "Not set"}
                </p>
                {userData?.one_time_password && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${userData.otp_used ? "bg-cyber-green/10 text-cyber-green border border-cyber-green/20" : "bg-cyber-orange/10 text-cyber-orange border border-cyber-orange/20"}`}>
                    {userData.otp_used ? "Used" : "Unused"}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3 pt-6">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-dark-border text-silver hover:text-white hover:border-electric-blue/40 transition-all text-sm font-medium cursor-pointer whitespace-nowrap"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

